import { useEffect, useState, type ReactNode } from 'react';
import { useMeta } from './lib/meta';
import { setTitlePrefix } from './lib/useTitle';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { safeNext, useAuth } from './lib/auth';
import { api } from './lib/api';
import type { Me } from './lib/types';
import { AccountPanel, AppShell, EnvBadge, Loading, type NavGroup } from '@buckmason/cx-ui';
import { SignIn } from './routes/SignIn';
import { SetPassword } from './routes/SetPassword';
import { Home } from './routes/Home';
import { People } from './routes/People';
import { Account } from './routes/Account';

/** The hub's navigation: Home, then People for admins; the account menu (Your account, Sign out) at the foot. */
function useNav(openAccount: () => void) {
  const { me, isAdmin, signOut, setMe } = useAuth();
  const { pathname } = useLocation();
  const meta = useMeta().data;
  if (!me) return null;
  const apps = [
    { key: 'hub', name: 'Hub', url: '/', role: me.hubRoleName },
    ...me.apps.map((a) => ({ key: a.key, name: a.name, url: a.url, role: a.roleName })),
  ];
  const groups: NavGroup[] = [{ items: [{ label: 'Home', icon: 'house', to: '/', active: pathname === '/' }] }];
  if (isAdmin) {
    groups.push({ label: 'Administration', items: [{ label: 'People', icon: 'users', to: '/people', active: pathname.startsWith('/people') }] });
  }
  return {
    meta,
    nav: {
      current: 'hub',
      apps,
      groups,
      person: { name: me.name, role: me.hubRoleName },
      // Your account opens in place over the page (frame 94:292); its password link goes to /account.
      settings: { onSelect: openAccount },
      onSignOut: () => void signOut(),
      // The sun/moon beside the account control: the same save as Your account, Mode only.
      onModeChange: (appearance: 'light' | 'dark') => api.post<{ person: Me }>('/api/id/auth/preferences', { appearance }).then((r) => setMe(r.person)),
      linkAs: Link,
      footer: <EnvBadge variant="footer" meta={meta} />,
    },
  };
}

function RequireAdmin({ children }: { children: ReactNode }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  const { me, loading } = useAuth();
  const location = useLocation();
  const meta = useMeta().data;
  const [accountOpen, setAccountOpen] = useState(false);
  const shell = useNav(() => setAccountOpen(true));
  const { setMe } = useAuth();
  setTitlePrefix(meta?.env);

  if (loading) return <Loading what="your session" />;

  // These work before anyone is signed in. They carry the dev ribbon too: they are where a
  // password could be typed into the wrong environment (EnvBadge renders nothing off dev).
  const ribbon = <EnvBadge variant="ribbon" meta={meta} />;
  if (location.pathname === '/set-password') return <>{ribbon}<SetPassword /></>;
  if (!me) return <>{ribbon}<SignIn /></>;
  // Already signed in and sent here by an app: go straight back.
  if (location.pathname === '/sign-in') return <SignInBounce />;

  return (
    <AppShell nav={shell!.nav} ribbon={ribbon}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/account" element={<Account />} />
        <Route
          path="/people"
          element={
            <RequireAdmin>
              <People />
            </RequireAdmin>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <AccountPanel
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        person={{ name: me.name, role: me.hubRoleName }}
        preferences={me}
        save={(change) => api.post<{ person: Me }>('/api/id/auth/preferences', change).then((r) => r.person)}
        // Only the newest answer reaches `me` (an older one arriving late is dropped by the panel).
        onSaved={(person) => person && setMe(person as Me)}
        passwordHref="/account"
        passwordLabel="Change password"
      />
    </AppShell>
  );
}

/** An app sent a signed-in person to /sign-in (their tab was older than their session). */
function SignInBounce() {
  const { me } = useAuth();
  const next = me ? safeNext(new URLSearchParams(window.location.search).get('next'), me) : null;
  useEffect(() => {
    if (next) window.location.replace(next);
  }, [next]);
  return next ? <Loading what="your app" /> : <Navigate to="/" replace />;
}
